import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FiSearch, FiX, FiFolder } from "react-icons/fi";
import { useAuthFetch } from "../hooks/useAuthFetch";
import { usePwaGlobalSearch } from "../hooks/usePwaGlobalSearch";
import { openProjectFromOpportunity } from "../utils/projectNavigation";
import "./SearchBar.css";
import { useCompactNav } from "../utils/compactNav";

function highlightMatch(text, query, minLength) {
  if (!query || query.length < minLength) {
    return text;
  }

  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) {
    return text;
  }

  return (
    <>
      {text.slice(0, index)}
      <mark className="global-search__mark">{text.slice(index, index + query.length)}</mark>
      {text.slice(index + query.length)}
    </>
  );
}

function useIsMobileViewport() {
  return useCompactNav();
}

/**
 * Website global search panel.
 * Desktop: centered command palette. Mobile browser: full-screen sheet under header.
 * Uses the same project index/search as PWA global search.
 */
export function GlobalSearch({ isOpen, onOpen, onClose }) {
  const navigate = useNavigate();
  const { authFetch, isReady } = useAuthFetch();
  const inputRef = useRef(null);
  const isSelectingRef = useRef(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const isMobile = useIsMobileViewport();

  const {
    query,
    setQuery,
    clearQuery,
    results,
    isLoading,
    loadError,
    showMinCharsHint,
    showNoResults,
    hasProjects,
    minSearchLength,
  } = usePwaGlobalSearch(isOpen);

  const showIdleHint = query.trim().length === 0 && !isLoading && !loadError;

  const closeSearch = useCallback(() => {
    onClose?.();
    clearQuery();
    setActiveIndex(0);
  }, [onClose, clearQuery]);

  const handleSelect = useCallback(async (item) => {
    if (!isReady || isSelectingRef.current) {
      return;
    }

    isSelectingRef.current = true;
    setIsSelecting(true);
    try {
      await openProjectFromOpportunity({
        authFetch,
        navigate,
        opportunityId: item.opportunityId,
        projectRecord: item.project,
      });
      closeSearch();
    } catch (error) {
      console.error("Error opening project from global search:", error);
    } finally {
      isSelectingRef.current = false;
      setIsSelecting(false);
    }
  }, [authFetch, isReady, navigate, closeSearch]);

  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(0);
      return undefined;
    }

    const focusTimer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 50);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const isShortcut =
        (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k";

      if (isShortcut) {
        event.preventDefault();
        if (isOpen) {
          closeSearch();
        } else {
          onOpen?.();
        }
        return;
      }

      if (!isOpen || isSelecting) return;

      if (event.key === "Escape") {
        event.preventDefault();
        closeSearch();
        return;
      }

      if (event.key === "ArrowDown" && results.length > 0) {
        event.preventDefault();
        setActiveIndex((prev) => (prev + 1) % results.length);
        return;
      }

      if (event.key === "ArrowUp" && results.length > 0) {
        event.preventDefault();
        setActiveIndex((prev) => (prev - 1 + results.length) % results.length);
        return;
      }

      if (event.key === "Enter" && results[activeIndex]) {
        event.preventDefault();
        handleSelect(results[activeIndex]);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, results, activeIndex, closeSearch, onOpen, handleSelect, isSelecting]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      className={`global-search__overlay${isMobile ? " global-search__overlay--mobile" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Search projects"
    >
      <button
        type="button"
        className="global-search__backdrop"
        aria-label="Close search"
        onClick={closeSearch}
      />

      <div className={`global-search__palette${isMobile ? " global-search__palette--mobile" : ""}`}>
        <div className="global-search__palette-header">
          <div className="global-search__input-wrap">
            <FiSearch className="global-search__input-icon" aria-hidden="true" />
            <input
              ref={inputRef}
              type="search"
              inputMode="search"
              enterKeyHint="search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              className="global-search__input"
              placeholder="Search by project name or number…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              disabled={isSelecting}
            />
            {query.length > 0 && (
              <button
                type="button"
                className="global-search__clear"
                aria-label="Clear search"
                onClick={() => {
                  clearQuery();
                  inputRef.current?.focus();
                }}
                disabled={isSelecting}
              >
                <FiX aria-hidden="true" />
              </button>
            )}
          </div>
          {isMobile && (
            <button
              type="button"
              className="global-search__cancel"
              onClick={closeSearch}
              disabled={isSelecting}
            >
              Cancel
            </button>
          )}
        </div>

        <div className="global-search__body">
          {loadError && !hasProjects && (
            <p className="global-search__message global-search__message--error">
              {loadError}
            </p>
          )}

          {!loadError && isLoading && !hasProjects && (
            <p className="global-search__message">Loading projects...</p>
          )}

          {showIdleHint && hasProjects && (
            <div className="global-search__hint">
              <p className="global-search__hint-title">Jump to a project</p>
              <p className="global-search__hint-text">
                Search by project name, project number, or opportunity number.
                Type at least {minSearchLength} characters.
              </p>
            </div>
          )}

          {showMinCharsHint && (
            <p className="global-search__message">
              Type at least {minSearchLength} characters to search.
            </p>
          )}

          {showNoResults && (
            <p className="global-search__message">No projects found.</p>
          )}

          {results.length > 0 && (
            <section className="global-search__section">
              <h2 className="global-search__section-title">
                Projects
                <span className="global-search__section-count">{results.length}</span>
              </h2>
              <ul className="global-search__results" role="listbox">
                {results.map((item, index) => (
                  <li
                    key={item.opportunityId}
                    role="option"
                    aria-selected={index === activeIndex}
                  >
                    <button
                      type="button"
                      className={`global-search__result${
                        index === activeIndex ? " global-search__result--active" : ""
                      }`}
                      disabled={isSelecting}
                      onMouseEnter={() => !isMobile && setActiveIndex(index)}
                      onClick={() => handleSelect(item)}
                    >
                      <span className="global-search__result-icon" aria-hidden="true">
                        <FiFolder />
                      </span>
                      <span className="global-search__result-text">
                        <span className="global-search__result-name">
                          {highlightMatch(
                            item.projectName || "Untitled project",
                            query.trim(),
                            minSearchLength
                          )}
                        </span>
                        <span className="global-search__result-meta">
                          {item.projectNo ? `PID: ${item.projectNo}` : "PID: —"}
                          {item.opportunityNo ? ` · OID: ${item.opportunityNo}` : ""}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Desktop header trigger for global search.
 */
function SearchBar({ onOpen, isOpen = false }) {
  return (
    <div className="global-search">
      <button
        type="button"
        className="global-search__trigger"
        onClick={onOpen}
        aria-label="Open global search (Control+K)"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <FiSearch className="global-search__trigger-icon" aria-hidden="true" />
        <span className="global-search__trigger-label">Search projects…</span>
        <kbd className="global-search__shortcut-chip" aria-hidden="true">
          Ctrl+K
        </kbd>
      </button>
    </div>
  );
}

export default SearchBar;
